import {
  AgentModelSelection,
  ChatAttachment,
  ChatModelOptionsResponse,
  ChatRequest,
  ChatResponse,
  SELECTED_PRIVATE_FILE_STORAGE_KEY,
} from "../types/chat";
import httpClient from "../functions/httpClient"; // Ensure this path is correct
import { AI_NOTE_CHANGED_EVENT, AI_NOTE_TOOLS } from "../hooks/useNoteChangeListener";
import { AI_FILE_CHANGED_EVENT, AI_FILE_TOOLS } from "../hooks/useFileChangeListener";
import { AI_MEETING_CONTENT_CHANGED_EVENT, AI_MEETING_CONTENT_TOOLS } from "../hooks/useMeetingContentChangeListener";
import { AI_SKILL_CHANGED_EVENT, AI_SKILL_TOOLS } from "../hooks/useSkillChangeListener";

export const AI_FACT_FIND_CHANGED_EVENT = "ai-fact-find-changed";
const AI_FACT_FIND_TOOLS = new Set(["update_fact_find_fields", "add_fact_find_goal", "delete_fact_find_item"]);

export const AGENT_SESSION_STORAGE_KEY = 'prospo_agent_session_id';

export class AgentChatClient {
  private availableTools: any[] = [];
  /** Server-authoritative session id; set from the first response, echoed on subsequent calls. */
  private sessionId: string | null = null;

  getSessionId(): string | null {
    return this.sessionId;
  }

  /** Start a fresh conversation (used by "New chat"). */
  resetSession(): void {
    this.sessionId = null;
    try { localStorage.removeItem(AGENT_SESSION_STORAGE_KEY); } catch { /* noop */ }
  }

  /** Re-attach to an existing server session (called after loading history on mount). */
  resumeSession(sessionId: string): void {
    this.sessionId = sessionId;
  }

  async connect(): Promise<void> {
    try {
      await this.discoverTools();
      console.log("Agent chat client connected successfully");
    } catch (error) {
      console.error("Failed to connect agent chat client:", error);
      // Optional: Add retry logic here if needed
    }
  }

  async disconnect(): Promise<void> {
    console.log("Agent chat client disconnected");
  }

  // [REFACTORED] Uses httpClient instead of verbose fetch
  private async discoverTools(): Promise<void> {
    try {
      const data: any = await httpClient("/chat/tools");
      this.availableTools = data.tools || [];
      console.log(`Discovered ${this.availableTools.length} agent tools`);
    } catch (error) {
      console.error("Failed to discover agent tools:", error);
    }
  }

  // [NEW] Get Context-Aware Suggestions
  async getSuggestions(input: {
    currentRoute: string;
    hasConversation: boolean;
    latestUserMessage?: string;
    latestAssistantMessage?: string;
    latestTools?: string[];
    pendingTool?: string;
  }): Promise<string[]> {
    try {
      const response: any = await httpClient("/chat/suggestions", {
        method: "POST",
        body: input,
      });
      return response.suggestions || [];
    } catch (error) {
      console.warn("Failed to fetch suggestions", error);
      return ["Schedule meeting", "Search Clients", "Help"]; // Fallback
    }
  }

  async uploadPrivateAttachment(file: File, sessionId: string, onProgress: (progress: number) => void, clientId?: string): Promise<ChatAttachment> {
    const prepared: any = await httpClient('/chat/attachments/request-upload', {
      method: 'POST',
      body: { sessionId, fileName: file.name, mimeType: file.type || 'application/octet-stream', size: file.size, clientId },
    });
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', prepared.uploadUrl);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onerror = () => reject(new Error('The attachment upload failed.'));
      xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
      xhr.onload = () => xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`The attachment upload failed (${xhr.status}).`));
      xhr.send(file);
    });
    const confirmed: any = await httpClient(`/chat/attachments/${prepared.attachmentId}/confirm`, { method: 'POST' });
    return { ...confirmed.attachment, status: 'ready', progress: 100 };
  }

  async deletePrivateAttachment(attachmentId: string): Promise<void> {
    await httpClient(`/chat/attachments/${attachmentId}`, { method: 'DELETE' });
  }

  async getModelOptions(sessionId?: string): Promise<ChatModelOptionsResponse> {
    const query = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : '';
    const data: any = await httpClient(`/chat/model-options${query}`);
    return {
      providers: data.providers || [],
      defaultModel: data.defaultModel,
      selectedModel: data.selectedModel,
    };
  }

  // [REFACTORED] Uses httpClient
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      let selectedPrivateFile = request.context.selectedPrivateFile || null;
      try {
        const storedSelection = sessionStorage.getItem(SELECTED_PRIVATE_FILE_STORAGE_KEY);
        if (storedSelection) selectedPrivateFile = JSON.parse(storedSelection);
      } catch {
        // The in-memory context remains the fallback when sessionStorage is unavailable.
      }
      const data: any = await httpClient("/chat/message", {
        method: "POST",
        body: {
          message: request.message,
          mentionedClients: request.mentionedClients,
          mentionedFiles: request.mentionedFiles,
          mentionedTemplates: request.mentionedTemplates,
          mentionedSkills: request.mentionedSkills,
          mentionedRules: request.mentionedRules,
          attachmentIds: request.attachmentIds,
          context: { ...request.context, selectedPrivateFile },
          history: request.history,
          streaming: false,
          sessionId: this.sessionId || request.context.sessionId,
          modelSelection: request.modelSelection,
        },
        signal: request.signal,
      });

      if (data.sessionId) {
        this.sessionId = data.sessionId;
        try { localStorage.setItem(AGENT_SESSION_STORAGE_KEY, data.sessionId); } catch { /* noop */ }
      }

      if (data.toolOperations) {
        this.processToolOperations(data.toolOperations);
      }

      return {
        content: data.content,
        toolOperations: data.toolOperations,
        sessionId: data.sessionId,
        conversationClient: data.conversationClient ?? null,
        pendingAction: data.pendingAction ?? null,
        emailLink: data.emailLink ?? null,
        modelSelection: data.modelSelection,
      };
    } catch (error: any) {
      if (error.name === "AbortError") {
        throw error;
      }
      console.error("Failed to send message:", error);
      throw error;
    }
  }

  /**
   * Approve or decline a pending write/destructive action. The backend executes
   * (or skips) the action and returns the agent's follow-up message.
   */
  async confirmAction(
    toolCallId: string,
    approved: boolean,
    modelSelection?: AgentModelSelection,
  ): Promise<ChatResponse> {
    try {
      const data: any = await httpClient("/chat/confirm", {
        method: "POST",
        body: { sessionId: this.sessionId, toolCallId, approved, modelSelection },
      });

      if (data.sessionId) {
        this.sessionId = data.sessionId;
        try { localStorage.setItem(AGENT_SESSION_STORAGE_KEY, data.sessionId); } catch { /* noop */ }
      }

      if (data.toolOperations) {
        this.processToolOperations(data.toolOperations);
      }

      return {
        content: data.content,
        toolOperations: data.toolOperations,
        sessionId: data.sessionId,
        pendingAction: data.pendingAction ?? null,
        meetingLink: data.meetingLink ?? null,
        emailLink: data.emailLink ?? null,
        workflowLink: data.workflowLink ?? null,
        modelSelection: data.modelSelection,
      };
    } catch (error: any) {
      console.error("Failed to confirm action:", error);
      throw error;
    }
  }

  /**
   * Pick one of a multi-choice pending action's options (e.g. "delete
   * everything" vs "move to root"). This does NOT execute anything — the
   * backend re-previews with that option's args and returns a fresh, ordinary
   * approve/decline pendingAction; call confirmAction() on THAT to run it.
   */
  async selectPendingOption(
    toolCallId: string,
    optionIndex: number,
    modelSelection?: AgentModelSelection,
  ): Promise<ChatResponse> {
    try {
      const data: any = await httpClient("/chat/confirm", {
        method: "POST",
        body: { sessionId: this.sessionId, toolCallId, optionIndex, modelSelection },
      });

      if (data.sessionId) {
        this.sessionId = data.sessionId;
        try { localStorage.setItem(AGENT_SESSION_STORAGE_KEY, data.sessionId); } catch { /* noop */ }
      }

      return {
        content: data.content,
        toolOperations: data.toolOperations,
        sessionId: data.sessionId,
        pendingAction: data.pendingAction ?? null,
      };
    } catch (error: any) {
      console.error("Failed to select option:", error);
      throw error;
    }
  }

  async getConversationHistory(
    sessionId: string,
    opts?: { limit?: number; before?: number }
  ): Promise<{
    messages: Array<{
      role: 'user' | 'assistant';
      content: string;
      attachments?: import('../types/chat').ChatAttachment[];
      meetingLink?: { meetingId: string; title: string } | null;
      emailLink?: { route: string; state?: Record<string, string> } | null;
    }>;
    totalCount: number;
    hasMore: boolean;
    sessionId: string;
    conversationClient?: { id: string; name: string } | null;
  }> {
    try {
      void opts;
      const data: any = await httpClient(`/chat/history/${sessionId}`);
      return data;
    } catch (error) {
      console.error("Failed to load conversation history:", error);
      return { messages: [], totalCount: 0, hasMore: false, sessionId, conversationClient: null };
    }
  }

  async getSessions(cursor?: string): Promise<{
    sessions: Array<{
      sessionId: string;
      updatedAt: string;
      title: string | null;
      preview: string;
      messageCount: number;
    }>;
    nextCursor: string | null;
  }> {
    try {
      void cursor;
      const data: any = await httpClient('/chat/sessions');
      return data;
    } catch (error) {
      console.error("Failed to load sessions:", error);
      return { sessions: [], nextCursor: null };
    }
  }

  async renameSession(sessionId: string, title: string): Promise<string> {
    const data: any = await httpClient(`/chat/sessions/${sessionId}`, {
      method: "PATCH",
      body: { title },
    });
    return data.title;
  }

  // [REFACTORED] Uses httpClient
  async getAvailableToolsDetailed(): Promise<any> {
    try {
      return await httpClient("/chat");
    } catch (error) {
      console.error("Failed to get detailed tools information:", error);
      return { tools: [], toolsByCategory: {}, totalTools: 0 };
    }
  }

  // [REFACTORED] Uses httpClient
  async getServerStatus(): Promise<any> {
    try {
      return await httpClient("/chat/status");
    } catch (error: any) {
      console.error("Failed to get server status:", error);
      return { success: false, error: error.message };
    }
  }

  getAvailableTools(): any[] {
    return this.availableTools;
  }

  // =========================================================
  //  CRITICAL HANDLERS (PRESERVED FROM ORIGINAL CODE)
  // =========================================================

  private processToolOperations(operations: any[]) {
    console.log("🔄 Processing tool operations:", operations);

    operations.forEach((operation, index) => {
      console.log(`🔄 Processing operation ${index + 1}:`, operation);

      // Notes/Wiki tools don't follow the actionType-based shape below — they
      // just return their data directly — so this UI-refresh signal is keyed
      // on tool name instead of result.actionType.
      if (AI_NOTE_TOOLS.has(operation.tool) && operation.result) {
        window.dispatchEvent(
          new CustomEvent(AI_NOTE_CHANGED_EVENT, {
            detail: { tool: operation.tool, ...operation.result },
          }),
        );
      }

      // Client Files tools follow the same direct-data shape as Notes/Wiki — no
      // actionType, so the UI-refresh signal below is keyed on tool name too.
      if (AI_FILE_TOOLS.has(operation.tool) && operation.result) {
        window.dispatchEvent(
          new CustomEvent(AI_FILE_CHANGED_EVENT, {
            detail: { tool: operation.tool, ...operation.result },
          }),
        );
      }

      // Meeting content can be changed from the floating chat while the meeting
      // page is already open. Notify that page so it can silently refetch the
      // affected meeting instead of requiring a browser refresh.
      if (AI_MEETING_CONTENT_TOOLS.has(operation.tool) && operation.result) {
        window.dispatchEvent(
          new CustomEvent(AI_MEETING_CONTENT_CHANGED_EVENT, {
            detail: { tool: operation.tool, ...operation.result },
          }),
        );
      }

      if (AI_SKILL_TOOLS.has(operation.tool) && operation.result) {
        window.dispatchEvent(
          new CustomEvent(AI_SKILL_CHANGED_EVENT, {
            detail: { tool: operation.tool, ...operation.result },
          }),
        );
      }

      // Always refetch after a Fact Find write attempt. The live graph update and
      // secondary version-history write cross two services; even a partial error
      // must not leave the open form showing stale values.
      if (AI_FACT_FIND_TOOLS.has(operation.tool) && operation.result) {
        window.dispatchEvent(
          new CustomEvent(AI_FACT_FIND_CHANGED_EVENT, {
            detail: { tool: operation.tool, ...operation.result },
          }),
        );
      }

      // open_file_upload is NOT dispatched here — browsers refuse to open a
      // native file picker from an async event handler like this one (no
      // "user activation"). ChatInterface.tsx flags the message instead, so
      // ChatMessage.tsx can render a real button whose OWN click is the
      // genuine gesture that opens the picker. See useFileChangeListener.ts.

      // Follow standardized structure: look directly at operation.result
      if (
        operation.result &&
        operation.result.success !== undefined &&
        operation.result.actionType
      ) {
        const result = operation.result;
        console.log("📋 Found standardized result:", result);

        switch (result.actionType) {
          case "navigate":
            console.log("🧭 Handling navigation:", result);
            this.handleNavigation(result);
            break;
          case "openModal":
            console.log("🪟 Handling modal open:", result);
            this.handleModalOpen(result);
            break;
          case "multiStep":
            console.log("🔄 Handling multi-step:", result);
            this.handleMultiStep(result);
            break;
          case "inChat":
            console.log("💬 In-chat response:", result.guidance);
            break;
          case "data":
            console.log("📊 Data response:", result.data);
            break;
          case "error":
            console.error("❌ Error response:", result.message);
            break;
          default:
            console.log("❓ Unknown action type:", result.actionType);
        }
      } else {
        console.log(
          "⚠️ Invalid result structure - missing required fields (success, actionType)",
        );
      }
    });
  }

  private handleNavigation(instruction: any) {
    console.log("🧭 handleNavigation called with:", instruction);
    if (instruction.route) {
      // Use React Router navigation via custom event to avoid page reload
      const navigationEvent = new CustomEvent("ai-navigate", {
        detail: {
          route: instruction.route,
          params: instruction.params,
          guidance: instruction.guidance,
        },
      });
      window.dispatchEvent(navigationEvent);
      console.log(`✅ Dispatched navigation event for: ${instruction.route}`);

      // Also try the global function if available (backward compatibility)
      if ((window as any).navigateFromAI) {
        (window as any).navigateFromAI(instruction.route);
        console.log(
          `✅ Also called navigateFromAI function for: ${instruction.route}`,
        );
      }
    } else {
      console.log("❌ No route provided in navigation instruction");
    }
  }

  private handleModalOpen(instruction: any) {
    console.log("🪟 handleModalOpen called with:", instruction);
    if (instruction.modalName && instruction.modalParams) {
      // Dispatch custom event for modal opening
      const event = new CustomEvent("ai-open-modal", {
        detail: {
          modalName: instruction.modalName,
          modalParams: instruction.modalParams,
        },
      });
      window.dispatchEvent(event);
      console.log("📡 Dispatched ai-open-modal event");

      // Also try direct function call as fallback
      if ((window as any).openModalFromAI) {
        (window as any).openModalFromAI({
          modalName: instruction.modalName,
          modalParams: instruction.modalParams,
        });
        console.log("📞 Called openModalFromAI function");
      } else {
        console.log("⚠️ openModalFromAI function not available");
      }

      console.log(
        `✅ Modal opened: ${instruction.modalName}`,
        instruction.modalParams,
      );
    } else {
      console.log("❌ Missing modalName or modalParams:", instruction);
    }
  }

  private handleMultiStep(instruction: any) {
    console.log("🔄 handleMultiStep called with:", instruction);
    if (instruction.steps && Array.isArray(instruction.steps)) {
      console.log(`🔄 Executing ${instruction.steps.length} steps with delays`);
      // Execute steps in sequence with delays
      instruction.steps.forEach((step: any, index: number) => {
        setTimeout(() => {
          console.log(`🔄 Executing step ${index + 1}:`, step);
          switch (step.actionType) {
            case "navigate":
              this.handleNavigation(step);
              break;
            case "openModal":
              this.handleModalOpen(step);
              break;
          }
        }, index * 500); // 500ms delay between steps
      });
    } else {
      console.log("❌ No steps provided in multi-step instruction");
    }
  }
}
